import * as Radix from "@radix-ui/themes";
import { never, Vivid } from "@vendrinc/frontend-ts";
import * as React from "react";
import styles from "./AuthenticationDialog.module.css";
import { Dialog } from "./Dialog";
import { Switcher } from "./Switcher";
import { AuthenticationContext } from "~/contexts/authenticationContext";
import { ReactRouterStubsContext } from "~/contexts/reactRouterStubsContext";
import { RequestType } from "~/domain/privateOffers/answers";
import * as Analytics from "~/hooks/useAnalytics";
import { usePlatformAuthentication } from "~/hooks/usePlatformAuthenticated";
import { usePrefab } from "~/hooks/usePrefab";

const { Icon } = Vivid;

const DialogContext = React.createContext<{ close: () => void }>({
  close: () => undefined,
});

export type AuthenticationEmbedProps = {
  platformAppUrl: string;
  containerRef: React.RefObject<HTMLIFrameElement>;
  withRequestType?: RequestType | "private_offer_purchase_scope";
  onlyEmail?: boolean;
  forComparingPrices?: boolean;
  companyId?: string;
  channelTargetOrigin?: string;
};

export const AuthenticationFrame: React.FC<AuthenticationEmbedProps> =
  React.memo(function AuthenticationFrame({
    platformAppUrl,
    containerRef,
    withRequestType,
    onlyEmail,
    forComparingPrices,
    companyId,
    channelTargetOrigin,
  }) {
    const prefab = usePrefab();

    const includeCompanyId =
      prefab.get("experiment-price-estimate-email-screen") === "variant 1";

    const sandbox = [
      "allow-forms",
      "allow-scripts",
      "allow-same-origin",
      "allow-popups",
    ].join(" ");

    const params = new URLSearchParams({
      marketplaceLogin: "true",
    });

    switch (withRequestType) {
      case "price_check": {
        params.append("priceCheck", "true");
        break;
      }

      case "quote": {
        params.append("privateOffer", "true");
        break;
      }

      case "price_estimate": {
        params.append("priceEstimate", "true");
        break;
      }

      case "private_offer_purchase_scope": {
        params.append("privateOfferPurchaseScope", "true");
        break;
      }

      case undefined: {
        break;
      }

      default:
        return never(withRequestType);
    }

    if (onlyEmail) {
      params.append("onlyEmail", "true");
    }

    if (forComparingPrices) {
      params.append("forComparingPrices", "true");
    }

    if (companyId && includeCompanyId) {
      params.append("companyId", companyId);
    }

    if (channelTargetOrigin) {
      params.append("channelTargetOrigin", channelTargetOrigin);
    }

    return (
      <iframe
        id="platform-auth"
        title="Log in or Sign up"
        src={`${platformAppUrl}/signin?${params}`}
        width="100%"
        height="100%"
        sandbox={sandbox}
        className={styles.authFrame}
        ref={containerRef}
      ></iframe>
    );
  });

export const AuthenticationContainer = function (props: {
  platformAppUrl: string;
  redirectTo: string | null;
  onlyEmail?: boolean;
  forComparingPrices?: boolean;
  channelTargetOrigin?: string;
}) {
  const { revalidator, navigate } = React.useContext(ReactRouterStubsContext);
  const { identify, alias } = Analytics.useAnalytics();
  const dialogContext = React.useContext(DialogContext);
  const { identity } = React.useContext(AuthenticationContext);

  const [authSucceeded, setAuthSucceeded] = React.useState(false);

  const onRedirectIframe = React.useCallback(
    ({
      frame,
      redirectTo,
    }: {
      frame: React.RefObject<HTMLIFrameElement>;
      redirectTo: string;
    }) => {
      if (frame.current) {
        if (frame.current.src !== redirectTo) {
          frame.current.src = redirectTo;
        }
      }
    },
    [],
  );

  const {
    frameRef,
    isAuthenticatedWithVendrPlatform,
    authenticationInProgress,
    disconnect,
  } = usePlatformAuthentication({
    onAuthSucceeded: React.useCallback(() => {
      setAuthSucceeded(true);

      if (props.redirectTo !== null) {
        navigate(props.redirectTo);
      }
    }, [navigate, props.redirectTo]),
    onRedirectIframe,
  });

  const checkForAuthenticatedState = React.useCallback(() => {
    if (identity) {
      disconnect();
      dialogContext.close();
      frameRef.current = null;

      identify(identity);

      if (identity.userId) {
        alias(identity.userId);
      }
    }

    if (!isAuthenticatedWithVendrPlatform || revalidator.state === "loading") {
      return;
    }

    revalidator.revalidate();
  }, [
    isAuthenticatedWithVendrPlatform,
    revalidator,
    identity,
    dialogContext,
    frameRef,
    disconnect,
    identify,
    alias,
  ]);

  React.useEffect(() => {
    if (isAuthenticatedWithVendrPlatform) {
      checkForAuthenticatedState();
    }
  });

  const { platformAppUrl } = props;

  const auth = React.useMemo(
    () =>
      !authSucceeded ? (
        <AuthenticationFrame
          containerRef={frameRef}
          platformAppUrl={platformAppUrl}
          onlyEmail={props.onlyEmail}
          forComparingPrices={props.forComparingPrices}
          channelTargetOrigin={props.channelTargetOrigin}
        />
      ) : null,
    [
      frameRef,
      platformAppUrl,
      authSucceeded,
      props.onlyEmail,
      props.forComparingPrices,
      props.channelTargetOrigin,
    ],
  );

  const spinner = React.useMemo(
    () => (
      <div className={styles.spinnerContainer}>
        <Vivid.Spinner />
      </div>
    ),
    [],
  );

  return (
    <Switcher
      content={auth}
      overlay={spinner}
      showOverlay={authenticationInProgress}
    />
  );
};

export const AuthenticationDialog: React.FC<{
  platformAppUrl: string;
  dialogIsOpen: boolean;
  redirectTo: string | null;
  setDialogIsOpen: (isOpen: boolean) => void;
  onlyEmail?: boolean;
  forComparingPrices?: boolean;
  channelTargetOrigin?: string;
}> = ({
  platformAppUrl,
  dialogIsOpen,
  setDialogIsOpen,
  redirectTo,
  onlyEmail,
  forComparingPrices,
  channelTargetOrigin,
}) => {
  const [transitionIsPending, startTransition] = React.useTransition();

  const setOpenClosed = React.useCallback(
    ({ isOpen: shouldBeOpen }: { isOpen: boolean }) => {
      if (transitionIsPending) {
        return;
      }

      if (shouldBeOpen === dialogIsOpen) {
        return;
      }

      startTransition(() => {
        if (shouldBeOpen === dialogIsOpen) {
          return;
        }
      });

      setDialogIsOpen(shouldBeOpen);
    },
    [transitionIsPending, dialogIsOpen, setDialogIsOpen],
  );

  const close = React.useCallback(() => {
    setOpenClosed({ isOpen: false });
  }, [setOpenClosed]);

  const { trackButton } = Analytics.useAnalytics();

  return (
    <Dialog
      isOpen={dialogIsOpen}
      onOpenChange={setOpenClosed}
      isPadded={true}
      a11yTitle="Log in or Sign in"
      a11yDescription="A form to log into or sign up for Vendr"
    >
      <Radix.Flex
        direction="row-reverse"
        className={styles.closeButtonContainer}
        pr="6"
        pt="2"
      >
        <Radix.Button
          onClick={() => {
            trackButton({
              element_text: "Close icon",
              element_location: "Authentication modal",
            });

            close();
          }}
          variant="ghost"
          className={styles.closeButton}
        >
          {Icon.close}
        </Radix.Button>
      </Radix.Flex>

      <DialogContext.Provider value={{ close }}>
        <AuthenticationContainer
          platformAppUrl={platformAppUrl}
          redirectTo={redirectTo}
          onlyEmail={onlyEmail}
          forComparingPrices={forComparingPrices}
          channelTargetOrigin={channelTargetOrigin}
        />
      </DialogContext.Provider>
    </Dialog>
  );
};

export type AuthenticationDialogProps = {
  children?: React.ReactNode;
  platformAppUrl: string;
};
